import type { VariantProps } from "class-variance-authority";
import { formatCellValue } from "@/components/contracts/table-utils";
import { DataTable } from "@/components/shared/data-table";
import { Badge, type badgeVariants } from "@/components/ui/badge";
import type { PrivateHire, PrivateHireStatus } from "@/lib/coach-manager/types";

interface PrivateHiresTableProps {
  privateHires: PrivateHire[];
  onSelect: (privateHire: PrivateHire) => void;
}

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;

const STATUS_VARIANTS: Record<PrivateHireStatus, BadgeVariant> = {
  Quotation: "secondary",
  Provisional: "warning",
  Firm: "success",
};

/**
 * A PrivateHire's status is two independent signals on the wire —
 * `PrivateHireStatus` (Quotation/Provisional/Firm) and a separate `Cancelled`
 * flag — collapsed here into a single combined badge (e.g. "Firm" or
 * "Provisional — Cancelled") rather than two separate badges.
 */
export function privateHireStatusBadge(privateHire: PrivateHire) {
  const variant: BadgeVariant = privateHire.Cancelled
    ? "destructive"
    : STATUS_VARIANTS[privateHire.PrivateHireStatus];
  const label = privateHire.Cancelled
    ? `${privateHire.PrivateHireStatus} — Cancelled`
    : privateHire.PrivateHireStatus;
  return { variant, label };
}

/**
 * The matrix list of matched PrivateHires — critical columns only. Clicking
 * anywhere in a row opens the detail drawer (`onSelect`), mirroring
 * `ContractsTable`.
 */
export function PrivateHiresTable({ privateHires, onSelect }: PrivateHiresTableProps) {
  return (
    <DataTable
      headers={[
        { label: "PrivateHire ID" },
        { label: "Client ID" },
        { label: "Booking Type" },
        { label: "Operator ID" },
        { label: "Date Created" },
        { label: "Movements" },
        { label: "Status" },
      ]}
    >
      {privateHires.map((privateHire) => {
        const status = privateHireStatusBadge(privateHire);
        return (
          <tr
            key={privateHire.PrivateHireID}
            className="cursor-pointer border-b last:border-b-0 hover:bg-muted/40"
            onClick={() => onSelect(privateHire)}
          >
            <td className="px-4 py-2">
              <span className="font-medium text-primary">{privateHire.PrivateHireID}</span>
            </td>
            <td className="px-4 py-2">{formatCellValue(privateHire.ClientID)}</td>
            <td className="px-4 py-2">{formatCellValue(privateHire.BookingType)}</td>
            <td className="px-4 py-2">{formatCellValue(privateHire.OperatorID)}</td>
            <td className="px-4 py-2">{formatCellValue(privateHire.DateCreated)}</td>
            <td className="px-4 py-2">{privateHire.PrivateHireMovements?.length ?? 0}</td>
            <td className="px-4 py-2">
              <Badge variant={status.variant}>{status.label}</Badge>
            </td>
          </tr>
        );
      })}
    </DataTable>
  );
}
